import {IDropdownMenuItem} from '@atomaro/ui-kit/components/DropdownMenu/types';
import {ITagGroupItem} from '@atomaro/ui-kit/components/Tag/TagGroup/types';
import {useApiariesStore} from 'entities/apiaries';
import {
  CalendarPatchRequestParams,
  CalendarPostRequestParams,
  useCalendarStore,
} from 'entities/calendar';
import {AddEntryParams, useDiaryStore} from 'entities/diary';
import {useDictionariesStore} from 'entities/dictionaries';
import {useHivesStore} from 'entities/hives';
import {useUserStore} from 'entities/user';
import {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ControllerRenderProps, useForm} from 'react-hook-form';
import {$api} from 'shared/api';
import {toRem} from 'shared/helpers';
import {useDebounceCallback} from 'shared/lib/debounce/useDebounce';
import {IDictionary, NewEntrieFormValues, NewEventFormValues, Owner} from 'shared/types';
import {allEqualNumber} from '../helpers/allEqualNumber';
import {dictMap} from '../helpers/dictMap';
import {CustomFieldsType} from 'shared/types/common';
import {pickerDateToUtcIso} from 'features/calendar/helpers/dates.ts';

const superDefaultValues: NewEntrieFormValues = {
  inspectedAt: new Date(),
  hives: [],
  frames: 0,
  strength: 0,
  brood: 0,
  note: '',
  fields: [],
  attachmentIds: [],
};

type useNewEntrieProps = {
  defaultValues?: Partial<NewEntrieFormValues>;
  defaultAuthor?: Owner;
  defaultDictionaries?: IDictionary[];
  defaultAttachments?: string[];
  editId?: string | number;
  editEventValues?: (NewEventFormValues & {id: string}) | null;
  fields: CustomFieldsType[];
};

export const useNewEntrie = ({
  defaultValues,
  defaultAuthor,
  defaultDictionaries,
  defaultAttachments,
  editId,
  editEventValues = null,
  fields,
}: useNewEntrieProps) => {
  const allDictionaries = useDictionariesStore((s) => s.dictionaries);
  const dictionaries = useMemo(
    () => allDictionaries.filter((el) => el.type === 'DIARY'),
    [allDictionaries]
  );

  const apiaryId = useApiariesStore((state) => state.currentApiaryId);
  const myInfo = useUserStore((state) => state.userInfo);
  const userInfo = defaultAuthor || myInfo;
  const hives = useHivesStore((state) => state.hives);
  const addDictionary = useDictionariesStore((s) => s.addDictionary);
  const getDictionariesFromServer = useDictionariesStore((s) => s.getDictionariesFromServer);
  const customFields = useDiaryStore((s) => s.customFields);
  const addEntry = useDiaryStore((s) => s.addEntry);
  const editEntry = useDiaryStore((s) => s.editEntry);
  const getDiaryEntriesFromServer = useDiaryStore((s) => s.getDiaryEntriesFromServer);
  const entries = useDiaryStore((s) => s.entries);

  const customDictionaries = useMemo<Record<string, IDictionary[]>>(() => {
    const dictFields = customFields.filter((f) => f.type === 'DICTIONARY');

    if (!dictFields.length) {
      return {};
    }

    const namesSet = new Set(dictFields.map((f) => f.name.toLowerCase()));
    const matched = allDictionaries.filter((d) => namesSet.has(d.type.toLowerCase()));
    const grouped: Record<string, IDictionary[]> = {};
    matched.forEach((dict) => {
      const type = dict.type;
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(dict);
    });

    return grouped;
  }, [customFields, allDictionaries]);
  // useEffect(() => {
  //   if (!defaultValues?.fields?.length) return;

  //   const initial: Record<string, Partial<IDictionary>[]> = {};

  //   for (const field of defaultValues.fields) {
  //     if (field.type !== "DICTIONARY") continue;

  //     const dicts = field.dictionaries ?? [];
  //     console.log("dicts", dicts)
  //     initial[field.id] = dicts.map(d => ({
  //       id: d.id,
  //       name: d.name,
  //       type: field.name,
  //     }));
  //   }
  //   setCustomDictionaryType(initial);
  // }, [defaultValues]);

  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});

  const [customDictionaryType, setCustomDictionaryType] = useState<
    Record<string, Partial<IDictionary>[]>
  >({});

  const [customDictionaryIds, setCustomDictionaryIds] = useState<Record<string, string[]>>({});

  const [customShowDictionaries, setCustomShowDictionaries] = useState<
    Record<string, IDictionary[]>
  >({});

  const [customIsDictionariesPopoverOpened, setCustomIsDictionariesPopoverOpened] = useState<
    Record<string, boolean>
  >({});
  const addEvent = useCalendarStore((s) => s.addEvent);
  const editEvent = useCalendarStore((s) => s.editEvent);

  const {
    control,
    formState: {errors},
    setValue,
    getValues,
    handleSubmit,
    reset,
    clearErrors,
    watch,
  } = useForm<NewEntrieFormValues>({
    defaultValues: {
      ...superDefaultValues,
      ...defaultValues,
      fields: defaultValues?.fields ?? fields ?? [],
    },
  });

  const formValues = watch();

  const [showFramesReset, setShowFramesReset] = useState<boolean>(getValues().frames > 0);
  const [showStrengthReset, setShowStrengthReset] = useState<boolean>(getValues().strength > 0);
  const [showBroodReset, setShowBroodReset] = useState<boolean>(getValues().brood > 0);
  const [activeDictionaries, setActiveDictionaries] = useState<Partial<IDictionary>[]>([]);
  const [commonDictionaryIds, setCommonDictionaryIds] = useState<
    {
      key: string;
      value: string;
    }[]
  >([]);
  const [showDictionaries, setShowDictionaries] = useState<IDictionary[]>(dictionaries);
  const [isDictionariesPopoverOpened, setIsDictionariesPopoverOpened] = useState<boolean>(false);
  const [noteLength, setNoteLength] = useState<number>(0);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [noteHeight, setNoteHeight] = useState<string>(toRem(76));

  const dictionariesRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentsRef = useRef<string[]>([]);

  const [entrieEvent, setEntrieEvent] = useState<CalendarPostRequestParams | null>(null);

  const [entrieEventHives, setEntrieEventHives] = useState<IDropdownMenuItem[]>(
    defaultValues?.hives ? hives.map((el) => ({key: el.id, value: `№${el.number}`})) : []
  );

  const [entrieEventHiveIds, setEntrieEventHiveIds] = useState<string[]>(
    defaultValues?.hives ? [`${defaultValues.hives}`] : []
  );

  const [isТewEventOpen, setIsNewEventOpen] = useState<boolean>(false);

  const tempFieldIdsRef = useRef<string[]>([]);

  const setDataFromLastEntrie = useDebounceCallback(() => {
    const hives = getValues('hives');
    if (!hives || editId) return;

    const entriesClone = structuredClone?.(entries) || JSON.parse(JSON.stringify(entries));
    entriesClone.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const lastEntries = hives
      .map((hive) => entriesClone.find((entry) => hive.key === entry.hive.id) || null)
      .filter((entry): entry is (typeof entries)[number] => entry !== null);

    if (!lastEntries.length) {
      setValue('inspectedAt', new Date());

      setValue('frames', 0);
      setValue('strength', 0);
      setValue('brood', 0);

      setValue('note', '');

      setValue('attachmentIds', []);
      setAttachments([]);

      setActiveDictionaries([]);

      return;
    }
    setValue('fields', []);
    const first = lastEntries[0];
    //console.log(lastEntries);
    // const date = new Date(finded.inspectedAt);

    const frames = allEqualNumber('frames', lastEntries, first) ? (first.frames ?? 0) : 0;
    let strength = allEqualNumber('strength', lastEntries, first) ? (first.strength ?? 0) : 0;
    let brood = allEqualNumber('brood', lastEntries, first) ? (first.brood ?? 0) : 0;

    // const note = finded.note;
    // const attachmentIds = finded.attachments.map(el => el.id);

    // setValue('date', date);

    if (frames < strength) {
      strength = frames;
    }

    if (strength < brood) {
      brood = strength;
    }

    if (brood > strength) {
      brood = strength;
    }

    setValue('frames', frames);
    setValue('strength', strength);
    setValue('brood', brood);

    // setValue("fields", fields);

    // setValue('note', note);

    // setValue('attachmentIds', attachmentIds);
    // setAttachments(attachmentIds);

    if (lastEntries.length <= 1) {
      const dicts = lastEntries[0].dictionaries ?? [];
      setActiveDictionaries(dicts);
      setCommonDictionaryIds([]);
      return;
    }
    const {allDictionaries, commonDictionaryIds} = dictMap(lastEntries);

    setActiveDictionaries(allDictionaries);
    setCommonDictionaryIds(commonDictionaryIds);
  }, 100);

  const onCustomDictionariesInputChange = (fieldKey: string, value: string, name: string) => {
    //console.log("Input changed:", value);
    setCustomIsDictionariesPopoverOpened((prev) => ({
      ...prev,
      [fieldKey]: true,
    }));
    // setCustomFieldValues((prev) => ({
    //   ...prev,
    //   [fieldKey]: value,
    // }));

    const arr = customDictionaries[name] ?? [];

    const filtered = arr.filter((el) => el.name.toLowerCase().includes(value.toLowerCase()));
    //console.log("Filtered dictionaries:", filtered);
    setCustomShowDictionaries((prev) => ({
      ...prev,
      [fieldKey]: filtered,
    }));
  };

  const onCustomDictionaryClick = (fieldKey: string, dictionary: IDictionary) => {
    setCustomDictionaryType((prev) => {
      const current = prev[fieldKey] ?? [];
      if (current.some((el) => el.id === dictionary.id)) return prev;

      return {
        ...prev,
        [fieldKey]: [...current, dictionary],
      };
    });

    setCustomIsDictionariesPopoverOpened((prev) => ({
      ...prev,
      [fieldKey]: false,
    }));
  };

  const onCustomDictionaryAdd = (fieldKey: string, addedTag: ITagGroupItem, name: string) => {
    setCustomDictionaryType((prev) => {
      const current = prev[fieldKey] ?? [];

      if (current.some((el) => el.name === addedTag.value)) {
        return prev;
      }
      // START
      // const id = dictionaries.find((el) => el.name === addedTag.value)?.id;

      // const addedDictionary: Partial<IDictionary> = {
      //   id: id || `new-${String(addedTag.key)}`,
      //   name: addedTag.value as string,
      // };
      //END

      const arr = customDictionaries[name];

      const existed = arr?.find(
        (el) => el.name.toLowerCase() === String(addedTag.value).toLowerCase()
      );

      const addedDictionary: Partial<IDictionary> = {
        id: existed?.id || `new-${String(addedTag.key)}`,
        name: addedTag.value as string,
        type: name,
      };
      return {
        ...prev,
        [fieldKey]: [...current, addedDictionary],
      };
    });
  };

  const onCustomDictionaryRemove = (fieldKey: string, removedTag: ITagGroupItem) => {
    setCustomDictionaryType((prev) => {
      const current = prev[fieldKey] ?? [];
      return {
        ...prev,
        [fieldKey]: current.filter((el) => el.id !== removedTag.key),
      };
    });
  };

  const setCustomIsDictionariesPopoverOpenedSafe = (fieldKey: string, isOpen: boolean) => {
    setCustomIsDictionariesPopoverOpened(() => ({
      [fieldKey]: isOpen,
    }));
  };

  const onDateChange = (activeDate?: Date) => {
    clearErrors();
    if (activeDate) {
      setValue('inspectedAt', activeDate);
    }
  };

  const onHiveChange = (value: IDropdownMenuItem[]) => {
    setValue('hives', value);
    clearErrors();

    setDataFromLastEntrie();
    if (!editEventValues?.id) {
      setEntrieEvent(null);
    }

    if (value) {
      setEntrieEventHives(hives.map((el) => ({key: el.id, value: `№${el.number}`})));
      setEntrieEventHiveIds([`${value}`]);
    }
  };

  const onFramesChange = (value: number) => {
    setValue('frames', value);
    setShowFramesReset(value > 0);
    clearErrors();
  };
  const onStrengthChange = (value: number) => {
    setValue('strength', value);
    setShowStrengthReset(value > 0);
    clearErrors();
  };
  const onBroodChange = (value: number) => {
    setValue('brood', value);
    setShowBroodReset(value > 0);
    clearErrors();
  };

  const onActiveDictionariesInputChange = (value: string) => {
    setIsDictionariesPopoverOpened(true);
    const arr = dictionaries.filter((el) =>
      el.name.toLocaleLowerCase().includes(value.toLocaleLowerCase())
    );
    setShowDictionaries(arr);
  };

  const onDictionaryClick = (dictionary: IDictionary) => {
    const finded = activeDictionaries.find((el) => el.id === dictionary.id);
    if (finded) return;

    setActiveDictionaries([...activeDictionaries, dictionary]);
  };

  const onActiveDictionaryAdd = (addedTag: ITagGroupItem) => {
    const finded = activeDictionaries.find((el) => el.name === addedTag.value);
    if (finded) {
      return;
    }

    const id = dictionaries.find((el) => el.name === addedTag.value)?.id;
    const addedDictionary = {
      id: id || `new-${addedTag.key as string}`,
      name: addedTag.value as string,
    };
    setActiveDictionaries([...activeDictionaries, addedDictionary]);
  };

  const onActiveDictionaryRemove = (removedTag: ITagGroupItem) => {
    setActiveDictionaries(activeDictionaries.filter((el) => el.id !== removedTag.key));
  };

  const addDictionaries = async (newDictionaries: Partial<IDictionary>[], type: string) => {
    //console.log("Adding dictionaries:", newDictionaries);
    if (!apiaryId) {
      //console.log("No apiaryId, cannot add dictionaries");
      return;
    }
    const arr = newDictionaries;
    for (let index = 0; index < newDictionaries.length; index++) {
      const newDictionary = newDictionaries[index];
      if (!newDictionary.name) continue;

      const resID = (
        await addDictionary({
          name: newDictionary.name,
          apiaryId,
          type: type ? type : 'DIARY',
          description: '',
        })
      ).data.id;

      //console.log("Added dictionary with ID:", resID);

      const idx = arr.findIndex((el) => el.id === newDictionary.id);
      if (idx >= 0) {
        arr[idx] = {
          id: resID,
          name: newDictionary.name,
        };
      }
    }

    if (apiaryId) {
      await getDictionariesFromServer(apiaryId);
    }

    return arr;
  };

  const onNoteChange = (
    event: ChangeEvent<HTMLTextAreaElement>,
    field: ControllerRenderProps<NewEntrieFormValues, 'note'>
  ) => {
    field.onChange(event);
    setNoteLength(event.target.value.length);
    calcTextAreaHeight();
  };

  const setCustomFieldValue = (fieldId: string, value: string) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));

    const prevFields = getValues('fields') as CustomFieldsType[] | undefined;

    const baseFields: CustomFieldsType[] =
      Array.isArray(prevFields) && prevFields.length ? prevFields : (customFields ?? []);

    const nextFields = baseFields.map((field) =>
      field.id === fieldId ? {...field, value} : field
    );

    setValue('fields', nextFields);
  };

  const getAttachment = async (files: FileList, index: number) => {
    const file = files[index];
    if (!file.type.includes('image')) {
      if (files[index + 1]) {
        return getAttachment(files, index + 1);
      }

      return;
    }

    const fd = new FormData();
    fd.set('file', file);
    const res = await $api.post('/attachments/files/upload', fd);

    const {id} = res.data;
    attachmentsRef.current = [...attachmentsRef.current, id];
    setAttachments(attachmentsRef.current);
    setValue('attachmentIds', [...getValues('attachmentIds'), id]);

    if (files[index + 1]) {
      return getAttachment(files, index + 1);
    }
  };

  const onFileChange = async () => {
    if (!fileInputRef.current) return;

    const files = fileInputRef.current.files;
    if (!files) return;

    await getAttachment(files, 0);
  };

  const onFileRemove = useCallback(
    (index: number) => {
      const arr = structuredClone?.(attachments) || JSON.parse(JSON.stringify(attachments));
      const arrIds = getValues('attachmentIds');
      arr.splice(index, 1);
      arrIds.splice(index, 1);

      setAttachments(arr);
      attachmentsRef.current = arr;
      setValue('attachmentIds', arrIds);
    },
    [attachments, attachmentsRef, setAttachments, setValue, getValues]
  );

  const createEvent = async (diaryId: string) => {
    if (!entrieEvent) return {status: 400};

    const res = await addEvent({...entrieEvent, diaryId}).catch(console.warn);

    if (res?.status === 200 || res?.status === 201) {
      setEntrieEvent(null);
    }

    return res;
  };

  const patchEvent = async () => {
    if (!editEventValues?.id || !apiaryId || !entrieEvent) return;
    setIsLoading(true);

    console.log(entrieEvent);
    const payload: CalendarPatchRequestParams = {
      id: editEventValues.id,
      startAt: entrieEvent.startAt,
      endAt: entrieEvent.endAt,
      description: entrieEvent.description,
      notifyAt: entrieEvent.notifyAt || null,
      notifyAtRepeat: entrieEvent.notifyAtRepeat || null,
    };

    await editEvent(payload).catch(console.warn);
  };

  const onSubmit = async (data: NewEntrieFormValues) => {
    try {
      //console.log("Form submitted with dataHook:", data);
      // return
      //console.log("activeDictionaries:", activeDictionaries);

      setIsLoading(true);
      const newDictionaries = activeDictionaries.filter((el) => el.id?.includes('new'));

      const oldDictionaries = activeDictionaries.filter((el) => !el.id?.includes('new'));

      const dictionariesForSend = await addDictionaries(newDictionaries, 'DIARY');
      //console.log("Dictionaries for send DIARY:", dictionariesForSend);

      const sendedCreatedDiaryArrIds =
        dictionariesForSend?.map((el) => el.id).filter((el) => el) || [];
      const sendedOldDiaryArrIds = oldDictionaries?.map((el) => el.id).filter((el) => el) || [];

      //console.log("Custom Dictionaries:", customDictionaryType);
      const createdDictsByUUID: Record<string, string[]> = {};

      for (const [uuid, dictionaries] of Object.entries(customDictionaryType)) {
        const newItems = dictionaries.filter((el) => el.id?.includes('new'));
        const existingItems = dictionaries.filter((el) => !el.id?.includes('new'));
        let createdIds: string[] = [];
        if (newItems.length > 0) {
          const type = newItems[0].type || 'DIARY';
          const res = await addDictionaries(newItems, type);

          if (res) {
            createdIds = res.map((e) => e.id).filter((id): id is string => Boolean(id));
          }
        }
        const existingIds = existingItems
          .map((e) => e.id)
          .filter((id): id is string => Boolean(id));
        createdDictsByUUID[uuid] = [...existingIds, ...createdIds];
      }

      //console.log("createdDictsByUUID:", createdDictsByUUID);
      if (!data.hives || !userInfo?.id || !apiaryId) return;
      if (customFields.length) {
        data.fields = data.fields
          .filter((field) => {
            return !(field.type !== 'DICTIONARY' && !field.value);
          })
          .map((field) => {
            if (field.type !== 'DICTIONARY') {
              return {...field};
            }
            return {
              ...field,
              dictionaryType: field.name,
              dictionaryIds: Array.isArray(createdDictsByUUID[field.id])
                ? [...createdDictsByUUID[field.id]]
                : [],
            };
          });
      }

      //console.log("Final form data before submission:", data.fields);
      // return

      const hiveIds = data.hives.map((h) => h.key);
      const baseRequestBody: Omit<AddEntryParams, 'hiveId' | 'id'> = {
        inspectedAt: data.inspectedAt,
        frames: data.frames,
        strength: data.strength,
        brood: data.brood,
        dictionaryIds: [...sendedCreatedDiaryArrIds, ...sendedOldDiaryArrIds],
        authorId: userInfo?.id,
        attachmentIds: data.attachmentIds,
        note: data.note,
        fields: data.fields,
      };
      //console.log("Base request body:", baseRequestBody);
      const getLastValuesForHive = (hiveId: string | number, entries: any[]) => {
        const entriesClone = structuredClone?.(entries) || JSON.parse(JSON.stringify(entries));
        entriesClone.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        const last = entriesClone.find((entry) => entry.hive.id === String(hiveId));

        if (!last) {
          return {frames: 0, strength: 0, brood: 0};
        }

        const frames = last.frames ?? 0;
        const strength = last.strength ?? 0;
        const brood = last.brood ?? 0;
        //console.log("Last entry for hive", hiveId, ":", last);

        return {frames, strength, brood};
      };

      if (editId) {
        const requestBody: AddEntryParams = {
          ...baseRequestBody,
          hiveId: hiveIds[0],
          id: editId,
        };

        const res = await editEntry(requestBody);
        await patchEvent();
        getDiaryEntriesFromServer({
          apiaryId,
          page: {index: 0, size: entries.length},
        });
        setIsLoading(false);
        return res;
      }
      const results = await Promise.all(
        hiveIds.map(async (hiveId) => {
          const prev = getLastValuesForHive(hiveId, entries);
          const bodyForHive: AddEntryParams = {
            ...baseRequestBody,
            hiveId,
            frames: formValues.hives.length > 1 ? prev.frames : baseRequestBody.frames,
            strength: formValues.hives.length > 1 ? prev.strength : baseRequestBody.strength,
            brood: formValues.hives.length > 1 ? prev.brood : baseRequestBody.brood,
          };
          try {
            return await addEntry(bodyForHive);
          } catch {
            return null;
          }
        })
      );
      const entrieID = results?.filter((r): r is NonNullable<typeof r> => !!r);
      if (entrieEvent && entrieID.length) {
        const firstId = entrieID[0].data.id;
        if (firstId) {
          await createEvent(firstId);
        }
      }

      getDiaryEntriesFromServer({
        apiaryId,
        page: {index: 0, size: entries.length + 1},
      });
      setIsLoading(false);

      return results.map((el) => el?.status);
    } catch (error) {
      console.error('Error in onSubmit:', error);
      setIsLoading(false);
      return;
    }
  };

  const clearDictionaries = () => {
    setActiveDictionaries([]);
  };

  const calcTextAreaHeight = useDebounceCallback(() => {
    const minHeight = 76;
    const val = getValues('note');
    const div = document.createElement('div');
    div.style.setProperty('padding', `${toRem(8)} ${toRem(12)}`);
    div.style.setProperty('max-width', toRem(376));
    div.innerHTML = val;
    document.body.append(div);

    let height = div.offsetHeight + 10;
    div.remove();
    if (height < minHeight) height = minHeight;

    setNoteHeight(toRem(height));
  }, 500);

  const onNewEventSubmit = (data: NewEventFormValues) => {
    if (!apiaryId) return;

    const hiveIds = data.hiveIds.length ? data.hiveIds : hives.map((el) => `${el.id}`);

    console.log(data);
    const payload: CalendarPostRequestParams = {
      apiaryId,
      hiveIds: hiveIds,
      startAt: data.startAtUtc || pickerDateToUtcIso(data.startAt),
      endAt: data.endAtUtc || pickerDateToUtcIso(data.endAt),
      description: data.description,
      notifyAt: data.notifyAt ? pickerDateToUtcIso(data.notifyAt) : null,
      notifyAtRepeat: data.notifyAtRepeat ? pickerDateToUtcIso(data.notifyAtRepeat) : null,
    };

    setEntrieEvent(payload);
    setIsNewEventOpen(false);
  };

  useEffect(() => {
    if (!isDictionariesPopoverOpened) {
      setShowDictionaries(dictionaries);
    }
  }, [isDictionariesPopoverOpened, dictionaries, setShowDictionaries]);

  useEffect(() => {
    if (defaultValues?.note?.trim()) {
      setValue('note', defaultValues?.note);
      calcTextAreaHeight();
    }
    if (defaultDictionaries?.length) {
      setActiveDictionaries(defaultDictionaries);
    }
    if (defaultAttachments?.length) {
      setAttachments(defaultAttachments);
      attachmentsRef.current = defaultAttachments;
    }
  }, [defaultValues, defaultDictionaries, defaultAttachments]);

  useEffect(() => {
    if (!customFields.length) return;

    setValue('fields', customFields);
  }, [customFields, setValue]);

  useEffect(() => {
    if (editEventValues && apiaryId) {
      const hiveValue = getValues('hives');
      if (hiveValue) {
        console.log('HERE');
        setEntrieEvent({
          apiaryId,
          hiveIds: [`${hiveValue}`],
          startAt: editEventValues.startAtUtc,
          endAt: editEventValues.endAtUtc,
          notifyAt: editEventValues.notifyAt
            ? pickerDateToUtcIso(editEventValues.notifyAt)
            : undefined,
          notifyAtRepeat: editEventValues.notifyAtRepeat
            ? pickerDateToUtcIso(editEventValues.notifyAtRepeat)
            : undefined,
          description: editEventValues.description,
          diaryId: editId ? `${editId}` : undefined,
        });
      }
    } else {
      setEntrieEvent(null);
    }
  }, [editEventValues]);

  return {
    control,
    showFramesReset,
    showStrengthReset,
    showBroodReset,
    activeDictionaries,
    showDictionaries,
    isDictionariesPopoverOpened,
    dictionariesRef,
    errors,
    noteLength,
    fileInputRef,
    attachments,
    userInfo,
    isLoading,
    hives,
    noteHeight,
    formValues,
    isТewEventOpen,
    entrieEvent,
    entrieEventHives,
    entrieEventHiveIds,
    setIsNewEventOpen,
    onDateChange,
    onHiveChange,
    onFramesChange,
    onStrengthChange,
    onBroodChange,
    handleSubmit,
    onSubmit,
    onActiveDictionariesInputChange,
    setIsDictionariesPopoverOpened,
    onActiveDictionaryAdd,
    onActiveDictionaryRemove,
    onDictionaryClick,
    reset,
    onNoteChange,
    onFileChange,
    setCustomFieldValue,
    onFileRemove,
    clearDictionaries,
    onNewEventSubmit,
    commonDictionaryIds,
    customFieldValues,
    customDictionaryType,
    customDictionaryIds,
    customShowDictionaries,
    customIsDictionariesPopoverOpened,
    onCustomDictionariesInputChange,
    onCustomDictionaryAdd,
    onCustomDictionaryRemove,
    onCustomDictionaryClick,
    setCustomIsDictionariesPopoverOpenedSafe,
    setCustomDictionaryIds,
    tempFieldIdsRef,
    setCustomIsDictionariesPopoverOpened,
    setCustomFieldValues,
  };
};
